import { useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import Spinner from '@/components/Common/Spinner';
import { ScrollArea } from '@/components/shadcn/ui/scroll-area';
import { Stage } from '@/components/Stage/Stage';
import {
  DYNAMIC_FILTER_QUERY_KEY,
  parseIssueDetailDateSelection,
  useIssueListQuery,
} from '@/features/(signal)/signals/hooks';
import { parseSignalDatePreset } from '@/features/(signal)/signals/lib';
import type { SignalDateSelection } from '@/features/(signal)/signals/model';
import SFilter from '@/features/StructuredFilter/class/SFilter';
import useStructuredFilterQuery from '@/features/StructuredFilter/hooks/useStructuredFilterQuery';
import useStructuredFilter from '@/hooks/domain/App/useStructuredFilter';
import { FEEDBACK_COLUMNS } from '@/types/filterKeyConst';
import { STRUCTURED_FILTER_OPERATOR } from '@/types/structuredFilterConst';

import { resolveIssueDetailDateRange } from '../lib/issue-detail-date-range';
import { useComputedIssue } from '../model/use-computed-issue';
import { CustomChartArea } from './custom-chart';
import { FilterArea } from './filter-area';
import { IssueDetailPageHeader } from './issue-detail-page-header';
import { IssueDetailPropertyArea } from './issue-detail-property-area';
import { IssueDetailSummary } from './issue-detail-summary';
import { VolumeChangeChartArea } from './volume-change-chart-area';
import { WordCloudArea } from './word-cloud-area';

export const IssueDetailPage = () => {
  const { parseStructuredFilter } = useStructuredFilter();

  const { issue_name } = useParams();
  if (!issue_name) throw new Error('Issue name is required');

  const [searchParams] = useSearchParams();
  const dynamicFilterSetString = searchParams.get(DYNAMIC_FILTER_QUERY_KEY);
  const dynamicFilterSet = dynamicFilterSetString
    ? (JSON.parse(dynamicFilterSetString) as StructuredFilter)
    : null;

  const structuredFiltreQueryState = useStructuredFilterQuery(
    undefined,
    dynamicFilterSet ? parseStructuredFilter(dynamicFilterSet) : undefined,
  );

  const [datePreset, setDatePreset] = useState<SignalDateSelection>(() =>
    parseIssueDetailDateSelection(searchParams),
  );

  const { unit } = parseSignalDatePreset(datePreset);

  const compositedFilter = useMemo(() => {
    const sFilter = new SFilter();
    sFilter.addFilterQueryFormats(
      structuredFiltreQueryState.filterQueryFormats,
    );

    const [startDate, endDate] = resolveIssueDetailDateRange({
      datePreset,
      mode: 'composited',
    });

    sFilter.setDateFilter({
      start_date: startDate.toJSON(),
      end_date: endDate.toJSON(),
    });

    return sFilter.get() || {};
  }, [
    datePreset,
    JSON.stringify(structuredFiltreQueryState.filterQueryFormats),
    searchParams,
  ]);

  const filterWithIssueName = useMemo(() => {
    const sFilter = new SFilter(compositedFilter);
    sFilter.addValueSet(FEEDBACK_COLUMNS.SIGNAL_ISSUE_NAME, {
      op: STRUCTURED_FILTER_OPERATOR.OR,
      values: [issue_name],
    });
    return sFilter.get() || {};
  }, [compositedFilter, issue_name]);

  // Chart용 1x 기간 filter (선택한 기간만, 비교 기간 제외)
  const chartFilter = useMemo(() => {
    const sFilter = new SFilter();
    sFilter.addFilterQueryFormats(
      structuredFiltreQueryState.filterQueryFormats,
    );

    const [startDate, endDate] = resolveIssueDetailDateRange({
      datePreset,
      mode: 'selected',
    });
    sFilter.setDateFilter({
      start_date: startDate.toJSON(),
      end_date: endDate.toJSON(),
    });

    return sFilter.get() || {};
  }, [
    datePreset,
    JSON.stringify(structuredFiltreQueryState.filterQueryFormats),
    searchParams,
  ]);

  const chartFilterWithIssueName = useMemo(() => {
    const sFilter = new SFilter(chartFilter);
    sFilter.addValueSet(FEEDBACK_COLUMNS.SIGNAL_ISSUE_NAME, {
      op: STRUCTURED_FILTER_OPERATOR.OR,
      values: [issue_name],
    });
    return sFilter.get() || {};
  }, [chartFilter, issue_name]);

  const {
    issueAggregates,
    originIssues,
    signals,
    aggregateFetching,
    originIssueFetching,
  } = useIssueListQuery({
    filterParams: compositedFilter || {},
    period: unit,
    issue: issue_name,
  });

  const originalIssue = originIssues.find((i) => i.name === issue_name);

  const compositedIssue = useComputedIssue({
    aggregatedIssue: issueAggregates[0],
    originIssues,
    issueName: issue_name,
    datePreset,
    signals,
  });

  if (!originalIssue) return null;

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      <IssueDetailPageHeader
        issue={originalIssue}
        filterSet={filterWithIssueName}
        datePreset={datePreset}
      />
      <FilterArea
        {...structuredFiltreQueryState}
        datePreset={datePreset}
        setDatePreset={setDatePreset}
      />
      <ScrollArea>
        <div
          className="flex justify-center w-full py-10 px-16"
          id="issue-detail-page-content-area"
        >
          <Stage className="flex flex-col h-full w-full max-w-4xl p-2">
            <Stage.Scene
              status={aggregateFetching || originIssueFetching}
              className="flex items-center justify-center min-h-100"
            >
              <Spinner />
            </Stage.Scene>
            <Stage.Scene status={!!compositedIssue} data={compositedIssue}>
              {(issue) => (
                <div className="flex flex-col gap-12 pb-40">
                  <IssueDetailPropertyArea
                    issue={issue}
                    filterSet={filterWithIssueName}
                    selectedPeriodFilterSet={chartFilterWithIssueName}
                    datePreset={datePreset}
                  />
                  <IssueDetailSummary
                    filterSet={chartFilterWithIssueName}
                    issue_name={issue_name}
                  />
                  <VolumeChangeChartArea
                    issue={issue}
                    filterSet={chartFilterWithIssueName}
                    datePreset={datePreset}
                  />
                  <WordCloudArea
                    issue={issue}
                    datePreset={datePreset}
                    filterSet={chartFilterWithIssueName}
                  />
                  <CustomChartArea
                    issue={issue}
                    datePreset={datePreset}
                    filterSet={chartFilterWithIssueName}
                  />
                </div>
              )}
            </Stage.Scene>
          </Stage>
        </div>
      </ScrollArea>
    </div>
  );
};
